import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const abandonedCartMachineConfig = {
  id: 'abandonedCartMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: {
          target: 'quote'
        },
        QUOTE_EXPIRED: {
          target: 'quoteExpired'
        }
      },
      meta: {
        path: routePath.abandonedCart
      }
    },

    quoteExpired: {
      on: {
        NEXT: [
          {
            target: 'energyNeeds',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ]
      },
      meta: {
        path: routePath.quoteExpiredAbandonedCart
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'energyNeeds'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ],
        MIXED_METERS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    energyNeeds: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.energyNeeds
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'externalBgSalesApp'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_ENERGY_NEEDS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    quote: {
      on: {
        NEXT: [
          {
            target: 'peakSave',
            guard: 'showPeakSaveRegistration'
          },
          {
            target: 'personalDetails'
          }
        ],
        CONSUMPTION: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    peakSave: {
      on: {
        NEXT: {
          target: 'personalDetails'
        }
      },
      meta: {
        path: routePath.peakSave
      }
    },

    personalDetails: {
      on: {
        NEXT: [
          {
            target: 'priorityServicesRegister',
            guard: 'hasPSR'
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.personalDetails
      }
    },

    priorityServicesRegister: {
      on: {
        NEXT: [
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        },
        CHANGE_PERSONAL_DETAILS: {
          target: 'personalDetails'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_PAYMENT_DETAILS: {
          target: 'directDebit'
        },
        CHANGE_PSR_DETAILS: {
          target: 'priorityServicesRegister'
        },
        CHANGE_ENERGY_NEEDS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    externalBgSalesApp: {
      meta: {
        path: actionUrl.bgSalesApp
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QEMBGyB2ED2HIGFkAnAFwFlkBjACwEs8A6a7AWzAGIA5AUQA0AVANoAGALqJQAB2yxaJWrgkgAHogDsAFgCsDAJzCtugBxGAbKYCMFrcKMAaEAE9EAJmEWGagMxbTRi-5aWmouakYAvuEOaJg4eBCEpBQ09GBMrBwAigCqAPL83AD6fAAKAJIAStwAIiLiSCDSsvKKDaoImjr6hibmVjb2TurCXp4aGkZqFiFBXqaR0ehYuATE5FR0jACOAK7YJGDcypK0RJBcfEJiSk1yChhK7f1qDF5qwiFeRsK6Grpag2cCFMvwYFhcLg03gCxkmLgWIBiy3iiXWKW2ewORxOZwgFwEggs9SkMjurVAT2sLzeHxcXx+fwBDiBkyMDCMLgscw0Ll+cyMGgRSLiqySG1SDF2+0Ox1O5x4BJcxMapJaDzaiGer3en2+v3+gPUGlGv2+nJGWisXl0QqWIoSa2SmzSbAORGk9BIADVkAAbWgQZBq-FXZW3NWPTVU7W0+n6plDYHgzy6f6Wnlma3zKKIu0rB1i9EusBuj0Yb1+gNB+4hwlh1X3SMILU03UMg3MxBeCFgibaYJfAKWbOLWL51FOiWusDu7Cen3+wPBgDi2TK1SK+AAMmV8ABpOo3BvklRR4Ixtvxw0IXwuPSaYQWoxeDTgm054Xjx3ixiwMC+sBKBIXJJDVWB2G4HdlzKAAhLcigAMWySDDwacNGw1Zspl0MFrFMMIBWEV9dFMTsEF+HQ-DeCx8JhTR31HZFRTRZ0GD-ACgJAsCIOqMp+EKABBapqiqABlUTUJJZoMIpTVuzZMI3i8R9UytFwyJI4RewzUwDF8GjbTHFFvyLNj-0A4DQPucCAAlcjIIoyFyL1uEklVpJPJ5sNw3wCI0IiLBIsiLCInQvBffxIQZFwBUMpiCxYiV2IsrjrPYOyHMKJyXLrI8PPVWSsMCnz8JMfziNIxMrFMSignBZTITMCE4vtCcfzSZLOKs3BbPsxznNcpU8rJArTyKnD+lKwiKuCiENG08xOl0L4apar9C1YzrLO4sgyl4Gosu4AoKgk640OPUavOKya-ICoLE1THQbF0FxTG0N63GmNbjI2iUwDwIgoEcTgwEgcCFVDYaI0w6ZjAYOaVJCox9D8MjxjvfSIRI3S1EewUPzzH7EsYf6ZyBkGwdrIkoZksbplMO9dFhqxhDcb5cbIsx5rmD54zUEJvG+5jJxJgHydBiBwcuQQhvO-Km0hUIGEfUxwq0OkqIsDSOTBVWGZi3Q1F8X4hYSkW0lJwHgYlqWCS8et5cwxWXhVtWNefLXEyMIJXh8fQ1BBAF-i8U22tMy3xcpiHBA0B2RoVnkXeEVWfHdrxPaBcEe2MJmdUTkj4QJozhfahgwAAN34YhaAAMxrsoMBr7AiBYatcFrM6pPjp3-jvdxfhq5b04hLwNKNsYzH8SxvdsNRQ5M1iK6roha-rxvm9b4No+puXu8K3l1eVwKNEH8Ks9HqrWaezQsZPpSgnn37GEkGdYFwP0G6blu24wUSdhYVuRBHAdzjtDQqL44aQmUhyR8NFloaGCiFHQPItBzGsJMSwTNH7EzSC-Igb8MAf3Xt-NUf8AHEGAfgGyAlODLiKEJES3BxJuXQp5Ls7hRghTehMAE-gPhaGCi+eaQRNCvi+IXRirUF4SjwQQohX9N73DIYAyh1DaFFG4DwCoy4ACahQeA1FOqA2m7ReTmAYH8NwPwDDGnCmoRBrMGDBFViRGBAQQ5F3imHViUoDggJpmwhAEC2RQNsFYuBL5OaoM8CCZa3xzAvTnp4qRT80i+I4NvYxgTgnw2NGE2BIJImJm8KMHwUw3oWHGOnOY2DzaSkxBwfAuROCiWyGQEo-AyjNJYRdJsylXxglQTyE+hspjqWKS+PQdIuRvG8DFCRuZi5m1Li-ZAABrUSyBy4ZOlp3dye8xr+WsLrVMuMVZM3ClE0YAdUz0gSaEWpKzX7v19NUEsyBaC+ltpDXeYDDn+QmhAzodJwoXxZKmJxB8aIxXTt7fGkj1o4IYLIl5bySAfK+VTLJl1EDjB+GCIF2gQWXK9n8TwIxXHGlxgCJJCKiZ1JRYQ157zPnfJltipsRydBwMNvoZOFywWakMKYeG1gKWVNhr4R5plJAr2bnIRwokZzl1oJQOAFQwBQFoLAN0-jfkmNxYk3sL1IRWGsLyYKz4Xb+QBCCSYrNXrStYrKhQK8SCKuVaq9VmrtW6syQEnFCBxjfF9jCkw1oOxVStcrG1ZhjDvAhCORZXjpGMAgHKICbzUByD1V3P57QBhaUfA+Iiojpicw8N2XkHxkYH1+Asz89LS7NwgDOZRFDc37Pza4XG8Mwh3xGZpE+wUoQ6DNK9dWgUAicidRKFtbb-4qPYFQmhdDCglG4CdZpAktyFA3PwASZQtxGIDQrXtoQBSq0HbpYdVVykMAZmELkyc8UMWTSkpF86iDtqAcutRa6GFiRPfqwJoQcIXoHbEm9lUgSch0PM3wj4AQ+C5BEZJiK6lfp-ao1dRQSgCR0Q5Tg-F92HuPT0x2+9z39qvVBoiMHXDFr7cjWeWgJgMzfY2kupksOLo7Su9R67RIVD3UdMjwG80GoQGB5jkHUzQeCv8F42hwq6X0CRGp6Gm08aIK279fHf0CbXZozduj9HcEMRRg5pjqOXpfHR29mcfBPWCOMVDhccwYGwK2+ADQuPLKLKezCABaBjCBQvK0fFF6L0XaXvow6XZgbAguFUsKMP4vN60gimIK6TxztAGDmOpxaFhZ0YmlNiOUEAUtjVceySwmgQiuJcEEMi7wTTggFN7AO3svpae46xacs55yViXLTVhgaXoitZsYK+z5UH+DIuFeamZ062Crd8LQZXixuhKHOcsy4dgBjADVp4Ng7xs0K7wvsZFfCcOIt8Y0UIYpxf894pK5kupgVO0Krk8MYo0VxtyZOnMXrw3pADkYIIoTbbLmLa2YMft5ZCK8Vm-lXovhmG1qEysME8iQyfB5-WAuL0rtXOun8N4-yR6EQwR9rABA5GYGi15jB3jPsEXkz4ouvcJgNmRzymWU5IUogzQJJPZIhGyLht4viaEZw4p6Q9qreAMAYWH6SkfyXmuMAOdq2MGwQRMnCvxHzIxNbYKVxP3vPzAOszZ2ytcChwnVSdYQarXm9iaN4y1RkQifbDxlfo0UYt8xLwNwyPD8w5KEIlIOva+HhpPetVJlJJre6m3Bcq3UeqICqtVsANVap1TOJHIyRW-CRiEXrIxLVvHhpb3oL0lK86WTbtI6aziZrANmkgSPLRg++CMMI7wmaOcQN1-7thnzVo5G9WHvHyFALL+MoE-x2RmCmO8CYXI9YL90zOfArBJAAQOP3hmFj6I-G8HfcEI7x6G1ZtYfpaWG185J39ZQbomUwSgJsgCsAAkkgkgTuf2NU3YdidIPwvgiuTiyugOykNgW21umeZcX+M4TKR+5YVAJA2QYeXaUmnIvIFiYifKoQIQwUtgWkRs-cbGqY4apWKBqSaB3+fov++AvoyAsAsglANkGQZA2AjuIGgaRBOEbm1oj45Bq+moc0rw4ICSNEUIbw6e7+7eLBGBfoAh2yDcNOb4JBXIZB-M0hzYNEY60ykIqunI4UkQkQQAA */
      ...abandonedCartMachineConfig
    },
    {
      guards
    }
  );
